import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, ChevronRight, Folder, Home, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  cloudSourceService,
  CloudProvider,
  FolderListItem,
} from '@/services/cloudSourceService';
import { logger } from '@/utils/logger';
import { getFriendlyErrorMessage } from '@/utils/errorMessage';

interface FolderPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceName: string;
  provider: CloudProvider;
  /** Comma-separated paths already saved on the source - pre-selected. */
  initialPaths?: string;
  /** Called with the final comma-separated list when the user confirms. */
  onConfirm: (paths: string) => void | Promise<void>;
}

interface FolderCache {
  // path → cached children for that folder (so back-navigation is instant)
  [parentPath: string]: FolderListItem[];
}

/**
 * Hierarchical folder picker shared by Google Drive and SharePoint
 * sources. The user navigates a tree starting from the source's root
 * (My Drive for Drive, the configured site's default document library
 * for SharePoint), drills in by clicking a folder name, and checks the
 * folders they want to sync. Breadcrumbs show the current path and
 * support back-navigation in a click.
 *
 * Multi-select: any folder in the tree can be ticked. Selected folders
 * persist across navigation (a check at "Sales" stays ticked when the
 * user drills into "Reports" and back). The "Save" button writes the
 * comma-joined paths back to the caller.
 *
 * Each level is fetched lazily via `cloudSourceService.listFolders`.
 * Already-fetched levels are cached so back-navigation is instant.
 */
const FolderPickerDialog: React.FC<FolderPickerDialogProps> = ({
  open,
  onOpenChange,
  sourceName,
  provider,
  initialPaths,
  onConfirm,
}) => {
  const [currentPath, setCurrentPath] = useState<string>('');
  const [cache, setCache] = useState<FolderCache>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // When the backend says the failure is recoverable by reconnecting (expired
  // token, missing scopes), surface a hint instead of a dead end.
  const [errorReconnect, setErrorReconnect] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  // When the dialog opens, reset state and seed selection from the
  // existing comma-separated paths on the source.
  useEffect(() => {
    if (!open) return;
    const seed = (initialPaths || '')
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);
    setSelected(new Set(seed));
    setCurrentPath('');
    setCache({});
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, sourceName]);

  // Fetch the current level whenever the user navigates somewhere we
  // haven't cached yet.
  const fetchLevel = useCallback(
    async (path: string) => {
      if (cache[path]) return; // already cached
      setLoading(true);
      setError(null);
      setErrorReconnect(false);
      try {
        const res = await cloudSourceService.listFolders(sourceName, path);
        if (res.status === 'ok') {
          setCache((prev) => ({ ...prev, [path]: res.folders }));
        } else {
          setError(res.message || 'Could not list folders.');
          setErrorReconnect(!!res.reconnect);
        }
      } catch (err: unknown) {
        // Network/transport failure before the API could answer.
        logger.error('listFolders failed', err);
        setError(
          'Couldn’t reach the server. Please check your connection and try again.',
        );
        setErrorReconnect(false);
      } finally {
        setLoading(false);
      }
    },
    [cache, sourceName],
  );

  useEffect(() => {
    if (!open) return;
    fetchLevel(currentPath);
  }, [open, currentPath, fetchLevel]);

  const currentItems = cache[currentPath] || [];

  // Breadcrumb segments derived from currentPath.
  const breadcrumbs = useMemo(() => {
    if (!currentPath) return [];
    const segs = currentPath.split('/').filter(Boolean);
    return segs.map((name, i) => ({
      name,
      path: segs.slice(0, i + 1).join('/'),
    }));
  }, [currentPath]);

  const toggleSelected = (path: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const list = Array.from(selected).join(', ');
      await onConfirm(list);
      onOpenChange(false);
    } catch (err: unknown) {
      const msg = getFriendlyErrorMessage(err, 'Failed to save.');
      toast.error('Save failed', { description: msg });
    } finally {
      setSaving(false);
    }
  };

  const rootLabel = provider === 'Google Drive' ? 'My Drive' : 'Documents';

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && !saving) onOpenChange(false);
      }}
    >
      <DialogContent className="dark bg-background text-foreground sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Pick folders to sync</DialogTitle>
          <DialogDescription>
            Browse {provider === 'Google Drive' ? 'your Google Drive' : 'this SharePoint site'} and tick the folders you want Orgain to sync. You can pick more than one.
          </DialogDescription>
        </DialogHeader>

        {/* Breadcrumb + back-to-root */}
        <div className="flex flex-wrap items-center gap-1 text-sm">
          <button
            type="button"
            onClick={() => setCurrentPath('')}
            className="flex items-center gap-1 rounded px-2 py-1 hover:bg-accent"
          >
            <Home className="h-3.5 w-3.5" />
            {rootLabel}
          </button>
          {breadcrumbs.map((b) => (
            <React.Fragment key={b.path}>
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              <button
                type="button"
                onClick={() => setCurrentPath(b.path)}
                className="rounded px-2 py-1 hover:bg-accent"
              >
                {b.name}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Folder list - scrollable */}
        <div className="max-h-72 min-h-32 overflow-y-auto rounded-md border">
          {loading ? (
            <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading folders…
            </div>
          ) : error ? (
            <div className="space-y-2 p-3 text-sm">
              <p className="text-destructive">{error}</p>
              {errorReconnect && (
                <p className="text-xs text-muted-foreground">
                  Close this dialog and use “Reconnect” on the source card to
                  grant access, then try picking folders again.
                </p>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchLevel(currentPath)}
              >
                Try again
              </Button>
            </div>
          ) : currentItems.length === 0 ? (
            <div className="p-3 text-sm text-muted-foreground">
              No sub-folders here.
            </div>
          ) : (
            currentItems.map((f) => {
              const isChecked = selected.has(f.path);
              return (
                <div
                  key={f.path}
                  className={
                    'flex items-center gap-2 border-b px-3 py-2 last:border-b-0 hover:bg-accent ' +
                    (isChecked ? 'bg-accent/50' : '')
                  }
                >
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={() => toggleSelected(f.path)}
                    aria-label={`Select ${f.name}`}
                  />
                  <button
                    type="button"
                    onClick={() => f.has_subfolders && setCurrentPath(f.path)}
                    className="flex flex-1 items-center gap-2 text-left"
                    disabled={!f.has_subfolders}
                  >
                    <Folder className="h-4 w-4 text-muted-foreground" />
                    <span className="flex-1 text-sm">{f.name}</span>
                    {f.has_subfolders && (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* Selection summary */}
        {selected.size > 0 && (
          <div className="space-y-1">
            <div className="text-xs font-medium text-muted-foreground">
              Selected ({selected.size}):
            </div>
            <div className="flex flex-wrap gap-1">
              {Array.from(selected).map((p) => (
                <span
                  key={p}
                  className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs"
                >
                  {p}
                  <button
                    type="button"
                    onClick={() => toggleSelected(p)}
                    className="rounded-sm hover:bg-muted-foreground/20"
                    aria-label={`Remove ${p}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save ({selected.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FolderPickerDialog;
